// src/apiClient.ts
import axios, { AxiosInstance } from 'axios';
// eslint-disable-next-line
import { Configuration } from './api/configuration';
import {
  SeasonApi,
  FacilityApi,
  CourseApi,
  StrategyApi,
  ChannelApi,
  SeasonBatchApi,
  PricingApi,
  UtilizationPriceApi,
} from './api/api';
import { AuthService } from './services/auth-service';
import { loadingService } from './services/loading-service';

// Update the getAuthToken function
const getAuthToken = (): string | null => {
  return AuthService.getToken();
};

// Get baseURL from environment variables
const baseURL = process.env.VUE_APP_BASE_URL;

if (!baseURL) {
  throw new Error(
    'VUE_APP_BASE_URL environment variable is not set. Please configure the environment variable for this build.'
  );
}

// Optional: Log the baseURL to verify it's loaded correctly
console.log('API Base URL:', baseURL);

// Create a custom Axios instance
const axiosInstance: AxiosInstance = axios.create({
  baseURL: baseURL,
});

// Request interceptor to add the JWT to headers and start loading
axiosInstance.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    loadingService.startLoading(); // Start loading before the request is sent
    return config;
  },
  (error) => {
    loadingService.stopLoading(); // Stop loading if there's a request error
    return Promise.reject(error);
  }
);

// Response interceptor to handle loading and global errors
axiosInstance.interceptors.response.use(
  (response) => {
    loadingService.stopLoading(); // Stop loading when a response is received
    return response;
  },
  (error) => {
    loadingService.stopLoading(); // Stop loading on response error
    // Handle specific status codes or errors globally
    if (error.response && error.response.status === 401) {
      console.error('Unauthorized! Redirecting to login.');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

const apiConfig = new Configuration({
  basePath: baseURL,
});

// Initialize API clients using the factory interfaces and the custom Axios instance
const seasonsApi = new SeasonApi(apiConfig, undefined, axiosInstance);
const facilityApi = new FacilityApi(apiConfig, undefined, axiosInstance);
const courseApi = new CourseApi(apiConfig, undefined, axiosInstance);
const strategyApi = new StrategyApi(apiConfig, undefined, axiosInstance);
const channelApi = new ChannelApi(apiConfig, undefined, axiosInstance);
const seasonBatchApi = new SeasonBatchApi(apiConfig, undefined, axiosInstance);
const pricingApi = new PricingApi(apiConfig, undefined, axiosInstance);
const utilizationPriceApi = new UtilizationPriceApi(
  apiConfig,
  undefined,
  axiosInstance
);

export {
  seasonsApi,
  facilityApi,
  courseApi,
  strategyApi,
  channelApi,
  seasonBatchApi,
  pricingApi,
  utilizationPriceApi,
};
